import notificationHndb from '../seller/templates/notification.handlebars';
import { Modal } from './modal';

/**
 * A notification modal that can display different types of messages.
 */
export default class Notification {
    /**
     * Creates a new Notification instance.
     * @param {Object} [data] - The notification data.
     * @param {string} [data.type=''] - The type of notification (e.g., 'warning', 'error', 'success', 'loading'). Case-insensitive.
     * @param {string} [data.title=''] - The title of the notification.
     * @param {string} [data.message=''] - The message of the notification.
     * @param {{fn: Function | null, content: string | null}} [data.onDefault] - Optional callback for the submit action.
     * @param {{fn: Function | null, content: string | null}} [data.onAlternative] - Optional callback for the cancel action.
     */
    constructor({
        type = '',
        title = '',
        message = '',
        onDefault = {},
        onAlternative = {},
    } = {}) {
        this.data = {
            type,
            title,
            message,
            onDefault: {
                fn: onDefault.fn ?? null,
                content: onDefault.content ?? null,
            },
            onAlternative: {
                fn: onAlternative.fn ?? null,
                content: onAlternative.content ?? null,
            },
        };
        this.id = Math.floor(Math.random() * 100);
    }

    notificationEl = null;

    modal = new Modal({
        padding: '0px',
        width: 'auto',
        containerId: this.id,
    });

    #update = () => {
        const typeLower = this.data.type.toLowerCase();
        const notificationEl = document.createElement('div');
        notificationEl.id = `os-notification-${this.id}`;
        notificationEl.innerHTML = notificationHndb({
            ...this.data,
            warning: typeLower === 'warning',
            error: typeLower === 'error',
            success: typeLower === 'success',
            loading: typeLower === 'loading',
        });
        this.modal.update(notificationEl);
        this.notificationEl = this.modal.modalContainer;
        this.#listeners();
    };

    #listeners = () => {
        if (this.data.onDefault.fn) {
            this.notificationEl
                .querySelector('#os-notification-action')
                ?.addEventListener('click', () => 
                    this.data.onDefault.fn(this)
                );
        } else {
            this.notificationEl
                .querySelector('#os-notification-action')
                ?.addEventListener('click', () => {
                    this.close();
                });
        }

        if (this.data.onAlternative.fn) {
            this.notificationEl
                .querySelector('#os-notification-alt-action')
                ?.addEventListener('click', () =>
                    this.data.onAlternative.fn(this)
                );
        } else {
            this.notificationEl
                .querySelector('#os-notification-alt-action')
                ?.addEventListener('click', () => {
                    this.close();
                });
        }
    };

    /**
     * Closes the notification modal.
     */
    close() {
        this.modal.close();
    }

    /**
     * Reloads the notification with new data.
     * @param {Object} [data] - The new notification data.
     * @param {string} [data.type=''] - The type of notification (e.g., 'warning', 'error', 'success', 'loading'). Case-insensitive.
     * @param {string} [data.title=''] - The title of the notification.
     * @param {string} [data.message=''] - The message of the notification.
     * @param {{fn: Function, content: string}} [data.onDefault] - Optional callback for the submit action.
     * @param {{fn: Function, content: string}} [data.onAlternative] - Optional callback for the cancel action.
     */
    reload({
        type = '',
        title = '',
        message = '',
        onDefault = {},
        onAlternative = {},
    } = {}) {
        this.data = {
            type,
            title,
            message,
            onDefault: {
                fn: onDefault.fn ?? null,
                content: onDefault.content ?? null,
            },
            onAlternative: {
                fn: onAlternative.fn ?? null,
                content: onAlternative.content ?? null,
            },
        };
        this.#update();
    }

    /**
     * Starts and displays the notification.
     */
    open() {
        this.modal.open();
        this.#update();
    }
}
